import { useState, useEffect } from "react";
import { Upload, Trash2, Star } from "lucide-react";
import {
  FiEdit2,
  FiFilter,
  FiPlus,
  FiPlusCircle,
  FiRotateCcw,
  FiSearch,
} from "react-icons/fi";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useHouseDesign, useBuyPlans } from "../useHook";
import Swal from "sweetalert2";
import SimilarItemsModal from "../../../Components/Modals/SimilarItems/SimilarItems";
import AddPopularDesign from "../../../Components/Modals/PopulaHouseDesign/PopularHouseDesing";
import RecordPosition from "../../../Components/Modals/RecordPosition/RecordPosition";
import { useNavigate, useSearchParams } from "react-router-dom";
import GlobalPagination from "../../../Components/Pagination/GlobalPagination";
import { IoEye } from "react-icons/io5";
import { MdDelete } from "react-icons/md";
import { usePermission, SystemPermissions } from "../../../Utils/permissionAccess";
import { sanitizePositionInput } from "../../../Utils/positionInput";
import { UrduInput } from "../../../Components/Shared/UrduInput";
import { ImageSeoFields } from "../../../Components/Shared/ImageSeoFields";
import {
  ASSOCIATED_HOUSE_OPTIONS,
  STANDARD_PLAN_BAND_OPTIONS,
  urduFor,
} from "../../../Utils/staticDropdownOptions";

const HouseDesign = () => {
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(SystemPermissions.HouseDesign.CREATE);
  const canUpdate = hasPermission(SystemPermissions.HouseDesign.UPDATE);
  const canDelete = hasPermission(SystemPermissions.HouseDesign.DELETE);
  const canSimilarDesign = hasPermission(
    SystemPermissions.HouseDesign.ADD_SIMILAR_DESIGN,
  );
  const canAddSlideShow = hasPermission(SystemPermissions.HouseDesign.ADD_SLIDE_SHOW);
  const canView = hasPermission(SystemPermissions.HouseDesign.VIEW);
  const canPosition = hasPermission(SystemPermissions.HouseDesign.POSITION);
  const showActions = canSimilarDesign || canView || canDelete || canUpdate;

  const navigate = useNavigate();
  const {
    getHouseDesign,
    addHouseDesign,
    updateHouseDesign,
    deleteHouseDesign,
    searchHouseDesign,
    updateHouseDesignPosition,
    getHouseDesignById,
    getAssociatedPlan,
    updateHouseDesignPrimaryImage,
  } = useHouseDesign();
  const { getPlans } = useBuyPlans();

  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [houseDesignData, setHouseDesignData] = useState([]);
  const [isSimilarModalOpen, setIsSimilarModalOpen] = useState(false);
  const [selectedDesignForSimilar, setSelectedDesignForSimilar] = useState(null);
  const [showPopularDesignModal, setShowPopularDesignModal] = useState(false);
  const [imageError, setImageError] = useState("");
  const [existingImages, setExistingImages] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [searchFilter, setSearchFilter] = useState("");
  const pageParam = parseInt(searchParams.get("page") || "0", 10);
  const offset = pageParam * limit;
  const [isPositionModalOpen, setIsPositionModalOpen] = useState(false);
  const [selectedDesignForPosition, setSelectedDesignForPosition] = useState(null);
  const [buyPlansList, setBuyPlansList] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    title_ur: "",
    plot_dimension: "",
    covered_area: "",
    floors: 0,
    bed_rooms: 0,
    bath_rooms: 0,
    associated_house: "",
    associated_house_ur: "",
    standard_plan_bands: "",
    standard_plan_band_ur: "",
    customUrl: "",
    description: "",
    description_ur: "",
    features_ur: "",
    servant_quarters: false,
    terrace: false,
    car_parking: false,
    custom_pricing_plan: false,
    selected_buy_plan_ids: [],
    images: [],
    imagePreviews: [],
    imagePositions: [],
    imageAlts: [],
    imageTitles: [],
    primaryImageIndex: 0, // Add primary image index
    // price: 0,
    // name: "",
  });
  useEffect(() => {
    loadHouseDesigns(offset, limit);
  }, [offset]);

  useEffect(() => {
    if (showModal && formData.custom_pricing_plan) {
      getPlans(setBuyPlansList);
    }
  }, [showModal, formData.custom_pricing_plan]);

  const handlePageChange = (newOffset) => {
    const newPage = Math.floor(newOffset / limit);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", String(newPage));
      return next;
    });
  };
  const closeSimilarModal = () => {
    setIsSimilarModalOpen(false);
    setSelectedDesignForSimilar(null);
  };
  const loadHouseDesigns = async (
    currentOffset = offset,
    currentLimit = limit,
    currentSearchFilter = searchFilter,
  ) => {
    setIsLoading(true);
    try {
      await getHouseDesign(
        setHouseDesignData,
        setTotal,
        currentOffset,
        currentLimit,
        currentSearchFilter,
      );
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load house designs. Please refresh the page.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      setIsLoading(true);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("page", "0");
        return next;
      });
      const response = await searchHouseDesign(searchFilter, 0, limit);
      const designs = Array.isArray(response?.data) ? response.data : [];
      setHouseDesignData(designs);
      const totalRecords =
        response?.pagination?.totalRecords ||
        response?.totalCount ||
        designs.length;
      setTotal(totalRecords);
    } catch (error) {
      console.error("Error in search:", error);
      Swal.fire({
        title: "Error",
        text: "There was an issue with the search.",
        icon: "error",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setSearchFilter("");
    setSearchParams({ page: "0" });
    loadHouseDesigns(0, limit, "");
  };
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === "checkbox" ? checked : value;
    setFormData((prev) => {
      const next = { ...prev, [name]: nextValue };
      if (name === "associated_house") {
        next.associated_house_ur = urduFor(ASSOCIATED_HOUSE_OPTIONS, nextValue);
      }
      if (name === "standard_plan_bands") {
        next.standard_plan_band_ur = urduFor(STANDARD_PLAN_BAND_OPTIONS, nextValue);
      }
      return next;
    });
  };
  const handleQuillChange = (value) => {
    setFormData({
      ...formData,
      description: value,
    });
  };

  const handleQuillChangeUr = (value) => {
    setFormData({
      ...formData,
      description_ur: value,
    });
  };
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const newValidImages = [];
    const newImagePreviews = [];

    const checkImageResolution = (file) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          if (img.width === 1600 && img.height === 900) {
            resolve(true);
          } else {
            resolve(false);
          }
        };
        img.src = URL.createObjectURL(file);
      });
    };

    Promise.all(files.map((file) => checkImageResolution(file))).then(
      (results) => {
        results.forEach((isValid, index) => {
          if (isValid) {
            newValidImages.push(files[index]);
            newImagePreviews.push(URL.createObjectURL(files[index]));
          } else {
            alert(
              `Image "${files[index].name}" does not have the required resolution of 1600*900. Please upload the correct resolution.`,
            );
          }
        });
        setFormData((prev) => {
          const currentImageCount = prev.images ? prev.images.length : 0;
          const newPrimaryIndex =
            currentImageCount === 0 ? 0 : prev.primaryImageIndex;
          const combinedCount = currentImageCount + newValidImages.length;
          let next = 1;
          const imagePositions = Array.from({ length: combinedCount }, (_, i) =>
            i === newPrimaryIndex ? "" : String(next++),
          );

          return {
            ...prev,
            images: [...(prev.images || []), ...newValidImages],
            imagePreviews: [...(prev.imagePreviews || []), ...newImagePreviews],
            imageAlts: [
              ...(prev.imageAlts || []),
              ...newValidImages.map(() => ""),
            ],
            imageTitles: [
              ...(prev.imageTitles || []),
              ...newValidImages.map(() => ""),
            ],
            imagePositions,
            primaryImageIndex: newPrimaryIndex,
          };
        });
      },
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!formData.customUrl || formData.customUrl.trim() === "") {
      setIsSubmitting(false);
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Custom URL is required. Please enter a custom URL.",
      });
      return;
    }

    if (formData.customUrl.trim().startsWith("/")) {
      setIsSubmitting(false);
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Custom URL cannot start with a forward slash (/). Please enter a valid URL.",
      });
      return;
    }

    if (
      (!formData.images || formData.images.length === 0) &&
      !(isEditing && existingImages.length > 0)
    ) {
      setImageError("At least one image is required");
      setIsSubmitting(false);
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please upload at least one image before submitting.",
      });
      return;
    }

    try {
      const apiData = new FormData();

      Object.keys(formData).forEach((key) => {
        if (
          key !== "image" &&
          key !== "images" &&
          key !== "imagePreviews" &&
          key !== "imagePositions" &&
          key !== "imageAlts" &&
          key !== "imageTitles" &&
          key !== "primaryImageIndex" &&
          key !== "selected_buy_plan_ids"
        ) {
          apiData.append(key, formData[key]);
        }
      });

      if (formData.selected_buy_plan_ids?.length > 0) {
        apiData.append(
          "selected_buy_plan_ids",
          JSON.stringify(formData.selected_buy_plan_ids),
        );
      }

      if (formData.images && formData.images.length > 0) {
        formData.images.forEach((image, index) => {
          apiData.append(`image`, image);
          const isPrimary = index === formData.primaryImageIndex;
          apiData.append(`isPrimary`, isPrimary);
          const position =
            formData.imagePositions?.[index] != null
              ? formData.imagePositions[index]
              : index + 1;
          apiData.append(`imagePosition`, position);
          const alt = formData.imageAlts?.[index];
          const title = formData.imageTitles?.[index];
          if (alt != null && String(alt).trim()) {
            apiData.append("imageAlt", String(alt).trim());
          }
          if (title != null && String(title).trim()) {
            apiData.append("imageTitle", String(title).trim());
          }
        });
      }

      if (isEditing && existingImages.length > 0) {
        existingImages.forEach((image) => {
          apiData.append(
            "existing_images",
            JSON.stringify({
              ...image,
              image_alt: image.image_alt || "",
              image_title: image.image_title || "",
            }),
          );
        });
      }

      let response;
      if (isEditing) {
        response = await updateHouseDesign(editId, apiData);
      } else {
        response = await addHouseDesign(apiData);
      }

      if (response && response.success === false) {
        throw new Error(response.message || "Failed to save house design.");
      }

      await loadHouseDesigns(offset, limit);

      setShowModal(false);
      setIsEditing(false);
      setEditId(null);
      setExistingImages([]);
      resetForm();

      Swal.fire({
        icon: "success",
        title: isEditing ? "House Design Updated" : "House Design Added",
        text: response?.message,
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      console.error("Failed to submit form:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error?.message ||
          "Failed to save house design. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  const resetForm = () => {
    if (formData.imagePreviews) {
      formData.imagePreviews.forEach((url) => {
        URL.revokeObjectURL(url);
      });
    }

    setFormData({
      title: "",
      title_ur: "",
      plot_dimension: "",
      covered_area: "",
      floors: 0,
      bed_rooms: 0,
      bath_rooms: 0,
      description: "",
      description_ur: "",
      features_ur: "",
      customUrl: "",
      associated_house: "",
      associated_house_ur: "",
      standard_plan_bands: "",
      standard_plan_band_ur: "",
      custom_pricing_plan: false,
      selected_buy_plan_ids: [],
      servant_quarters: false,
      terrace: false,
      car_parking: false,
      // price: 0,
      name: "",
      images: [],
      imagePreviews: [],
      imagePositions: [],
      imageAlts: [],
      imageTitles: [],
      primaryImageIndex: 0,
    });
  };
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        const response = await deleteHouseDesign(id);
        if (response && response.success === false) {
          throw new Error(response.message || "Failed to delete house design.");
        }
        await loadHouseDesigns(offset, limit);
        Swal.fire(
          "Deleted!",
          response?.message || "House design deleted successfully.",
          "success",
        );
      } catch (error) {
        console.error("Failed to delete house design:", error);
        Swal.fire(
          "Error!",
          error?.message || "Failed to delete house design. Please try again.",
          "error",
        );
      }
    }
  };
  const removeImage = (index) => {
    const updatedImages = [...formData.images];
    const updatedPreviews = [...(formData.imagePreviews || [])];
    const updatedPositions = [...(formData.imagePositions || [])];
    const updatedAlts = [...(formData.imageAlts || [])];
    const updatedTitles = [...(formData.imageTitles || [])];

    if (updatedPreviews[index]) {
      URL.revokeObjectURL(updatedPreviews[index]);
    }

    updatedImages.splice(index, 1);
    updatedPreviews.splice(index, 1);
    updatedPositions.splice(index, 1);
    updatedAlts.splice(index, 1);
    updatedTitles.splice(index, 1);

    // Adjust primary image index if the removed image was primary or before it
    let newPrimaryIndex = formData.primaryImageIndex;
    if (index <= formData.primaryImageIndex) {
      newPrimaryIndex = Math.max(0, formData.primaryImageIndex - 1);
    }

    setFormData({
      ...formData,
      images: updatedImages,
      imagePreviews: updatedPreviews,
      imagePositions: updatedPositions,
      imageAlts: updatedAlts,
      imageTitles: updatedTitles,
      primaryImageIndex: newPrimaryIndex,
    });
    if (updatedImages.length === 0) {
      setImageError("At least one image is required");
    }
  };

  const onImagePositionChange = (index, value) => {
    const cleaned = sanitizePositionInput(value);
    setFormData((prev) => {
      const images = [...(prev.images || [])];
      const positions = images.map(
        (_, i) => prev.imagePositions?.[i] ?? i + 1,
      );
      positions[index] = cleaned;
      return { ...prev, imagePositions: positions };
    });
  };

  const setPrimaryImage = (index) => {
    setFormData((prev) => {
      const images = prev.images || [];
      let next = 1;
      const imagePositions = images.map((_, i) =>
        i === index ? "" : String(next++),
      );
      return {
        ...prev,
        primaryImageIndex: index,
        imagePositions,
      };
    });
  };

  const onExistingImagePositionChange = (index, value) => {
    const cleaned = sanitizePositionInput(value);
    setExistingImages((prev) =>
      prev.map((img, i) => (i === index ? { ...img, position: cleaned } : img)),
    );
  };

  const onExistingImageSeoChange = (index, field, value) => {
    setExistingImages((prev) =>
      prev.map((img, i) => (i === index ? { ...img, [field]: value } : img)),
    );
  };

  const onNewImageSeoChange = (index, field, value) => {
    const key = field === "image_alt" ? "imageAlts" : "imageTitles";
    setFormData((prev) => {
      const next = [...(prev[key] || [])];
      next[index] = value;
      return { ...prev, [key]: next };
    });
  };

  const handleSetExistingPrimary = async (imageId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You want to change the primary image of this house design?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, change it!",
    });

    if (!result.isConfirmed) return;

    try {
      await updateHouseDesignPrimaryImage(imageId);
      setExistingImages((prev) => {
        let nextIndex = 1;
        return prev.map((img) => {
          if (img.id === imageId) {
            return { ...img, is_primary: true, position: "" };
          }
          return {
            ...img,
            is_primary: false,
            position: nextIndex++,
          };
        });
      });
      Swal.fire({
        title: "Success",
        text: "Primary image has been updated",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Failed to update primary image:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to update primary image. Please try again.",
        icon: "error",
      });
    }
  };

  const handlePageView = (id) => {
    navigate(`/house-design/${id}`);
  };

  const handleEdit = async (id) => {
    try {
      setIsSubmitting(true);
      const design = await getHouseDesignById(id);
      if (!design) {
        throw new Error("House design not found");
      }

      const toPlanId = (value) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
      };
      let selectedPlanIds = (
        Array.isArray(design.selected_buy_plan_ids)
          ? design.selected_buy_plan_ids
          : Array.isArray(design.buyPlans)
            ? design.buyPlans.map((plan) => plan.id || plan.buy_plan_id)
            : Array.isArray(design.buyOptions)
              ? design.buyOptions.map(
                  (opt) => opt.buy_plan_id || opt.buyPlan?.id,
                )
              : []
      )
        .map(toPlanId)
        .filter((planId) => planId != null);

      // test-api (and older builds) strip buyOptions; load associations separately
      if (selectedPlanIds.length === 0) {
        try {
          const associated = await getAssociatedPlan(() => {}, id);
          const rows = Array.isArray(associated?.data) ? associated.data : [];
          selectedPlanIds = rows
            .map((row) => row.buy_plan_id || row.buyPlan?.id || row.id)
            .map(toPlanId)
            .filter((planId) => planId != null);
        } catch (assocError) {
          console.warn("Failed to load buy plan associations:", assocError);
        }
      }

      setFormData({
        title: design.title || design.name || "",
        title_ur: design.title_ur || "",
        plot_dimension: design.plot_dimension || "",
        covered_area: design.covered_area || "",
        floors: design.floors || 0,
        bed_rooms: design.bed_rooms || 0,
        bath_rooms: design.bath_rooms || 0,
        associated_house: design.associated_house || "",
        associated_house_ur: design.associated_house_ur || "",
        standard_plan_bands:
          design.standard_plan_bands || design.standard_plan_band || "",
        standard_plan_band_ur: design.standard_plan_band_ur || "",
        customUrl: design.customUrl || "",
        description: design.description || "",
        description_ur: design.description_ur || "",
        features_ur: design.features_ur || "",
        servant_quarters: !!design.servant_quarters,
        terrace: !!design.terrace,
        car_parking: !!design.car_parking,
        custom_pricing_plan:
          !!design.custom_pricing_plan || selectedPlanIds.length > 0,
        selected_buy_plan_ids: selectedPlanIds,
        images: [],
        imagePreviews: [],
        imageAlts: [],
        imageTitles: [],
        primaryImageIndex: 0,
      });
      setExistingImages(
        (design.images || [])
          .map((img, index) => ({
            ...img,
            // Primary is excluded from indexing — never show/edit 0
            position: img.is_primary
              ? ""
              : img.position != null && Number(img.position) >= 1
                ? img.position
                : index + 1,
          }))
          .sort((a, b) => {
            if (!!a.is_primary !== !!b.is_primary) return a.is_primary ? -1 : 1;
            return (Number(a.position) || 0) - (Number(b.position) || 0);
          }),
      );
      setImageError("");
      setIsEditing(true);
      setEditId(id);
      setShowModal(true);
    } catch (error) {
      console.error("Failed to load house design for edit:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.message || "Failed to load house design for editing.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  useEffect(() => {
    return () => {
      if (formData.imagePreviews) {
        formData.imagePreviews.forEach((url) => {
          URL.revokeObjectURL(url);
        });
      }
    };
  }, [formData.imagePreviews]);
  const handleFeatureChange = (featureName) => {
    setFormData({
      ...formData,
      [featureName]: !formData[featureName],
    });
  };

  const handleCustomPricingPlanChange = () => {
    setFormData({
      ...formData,
      custom_pricing_plan: !formData.custom_pricing_plan,
      ...(formData.custom_pricing_plan ? { selected_buy_plan_ids: [] } : {}),
    });
  };

  const handleBuyPlanCheckboxChange = (planId) => {
    const normalizedId = Number(planId);
    setFormData((prev) => {
      const ids = (prev.selected_buy_plan_ids || []).map(Number);
      const isSelected = ids.includes(normalizedId);
      return {
        ...prev,
        selected_buy_plan_ids: isSelected
          ? ids.filter((id) => id !== normalizedId)
          : [...ids, normalizedId],
      };
    });
  };
  const handleAddSimilarDesign = (id) => {
    setSelectedDesignForSimilar(id);
    setIsSimilarModalOpen(true);
  };

  const handlePositionClick = (design) => {
    setSelectedDesignForPosition({
      recordId: design.id,
      currentPosition: design.position ?? 0,
    });
    setIsPositionModalOpen(true);
  };

  const handleUpdatePosition = async (recordId, newPosition) => {
    await updateHouseDesignPosition(recordId, newPosition);
    await loadHouseDesigns(offset, limit, searchFilter);
  };

  const closePositionModal = () => {
    setIsPositionModalOpen(false);
    setSelectedDesignForPosition(null);
  };

  return (
    <div>
      {/* Content */}
      <div className="">
        <div className="flex flex-wrap gap-4 justify-between items-center">
          <h3 className="text-xl font-bold">House Design</h3>

          <div className="flex flex-wrap gap-2">
            {canCreate && (
              <button
                onClick={() => {
                  resetForm();
                  setIsEditing(false);
                  setEditId(null);
                  setExistingImages([]);
                  setShowModal(true);
                }}
                className="btn btn-primary h-[44px]"
              >
                <FiPlus className="text-white" />
                Add House Design
              </button>
            )}

            {canAddSlideShow && (
              <button
                onClick={() => {
                  setShowPopularDesignModal(true);
                }}
                className="btn btn-primary h-[44px]"
              >
                <FiPlus className="text-white" />
                Add Slide Show
              </button>
            )}
          </div>
        </div>

        {/* Search Filter */}
        <div className="bg-white p-5 w-full rounded-lg pagination-shadow flex flex-wrap xlCustom:flex-nowrap md:justify-end items-center gap-3 my-5">
          <div className="search-wrapper">
            <input
              type="text"
              className="search-input"
              placeholder="Search house designs"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
            />
            <FiSearch className="search-icon" />
          </div>
          <div className="flex flex-wrap md:flex-nowrap gap-2">
            <button
              className="btn btn-filter filter-btn"
              onClick={handleSearch}
            >
              <FiFilter className="text-white" /> Apply Filter
            </button>
            <button
              className="btn btn-reset-filter filter-btn"
              onClick={handleReset}
            >
              <FiRotateCcw className="text-white" /> Reset Filter
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white w-full rounded-lg pagination-shadow">
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto bg-white rounded-lg  overflow-hidden">
              <thead>
                <tr className="brand-gradient text-white">
                  <th className="table-th">Sr. No.</th>
                  <th className="table-th">Title</th>
                  <th className="table-th">Plot Dimension</th>
                  <th className="table-th">Covered Area</th>
                  {showActions && <th className="table-th">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {houseDesignData.length > 0 ? (
                  houseDesignData.map((design, index) => (
                    <tr
                      key={design.id}
                      className={`${
                        index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                      } hover:bg-gray-200 transition-colors duration-300`}
                    >
                      <td className="table-td">
                        {canPosition ? (
                          <button
                            type="button"
                            onClick={() => handlePositionClick(design)}
                            className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-medium"
                          >
                            {design.position ?? "—"}
                          </button>
                        ) : (
                          <span className="font-medium text-gray-500">
                            {design.position ?? "—"}
                          </span>
                        )}
                      </td>
                      <td className="table-td">
                        {design.title || design.name}
                      </td>
                      <td className="table-td">{design.plot_dimension}</td>
                      <td className="table-td">{design.covered_area}</td>
                      {showActions && (
                        <td className="table-td">
                          <div className="flex space-x-2">
                            {canSimilarDesign && (
                              <button
                                className="btn btn-action"
                                onClick={() => handleAddSimilarDesign(design.id)}
                              >
                                <FiPlusCircle className="text-white" />
                                Similar Items
                              </button>
                            )}
                            {canUpdate && (
                              <button
                                className="btn btn-edit text-white"
                                onClick={() => handleEdit(design.id)}
                              >
                                <FiEdit2 className="text-white" /> Edit
                              </button>
                            )}
                            {canView && (
                              <button
                                className="btn btn-view"
                                onClick={() => handlePageView(design.id)}
                              >
                                <IoEye className="text-white" /> View
                              </button>
                            )}
                            {canDelete && (
                              <button
                                className="btn btn-delete text-white"
                                onClick={() => handleDelete(design.id)}
                              >
                                <MdDelete className="text-white" /> Delete
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={showActions ? 5 : 4}
                      className="py-4 px-4 text-center text-gray-500 font-bold"
                    >
                      {isLoading ? (
                        <div className="flex items-center justify-center">
                          <svg
                            className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Loading house designs...
                        </div>
                      ) : (
                        "No house designs found"
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {houseDesignData.length > 0 && total > limit && (
          <div className="mt-4">
            <GlobalPagination
              offset={offset}
              limit={limit}
              total={total}
              onPageChange={handlePageChange}
              className="bg-white"
            />
          </div>
        )}
      </div>

      {/* Add/Edit House Design Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg w-1/2 relative max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">
                {isEditing ? "Edit House Design" : "Add House Design"}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                    required
                  />
                  <UrduInput
                    label="عنوان"
                    name="title_ur"
                    value={formData.title_ur}
                    onChange={handleChange}
                  />
                </div>
                {/* <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                    required
                  />
                </div> */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Plot Dimension
                  </label>
                  <input
                    type="text"
                    name="plot_dimension"
                    value={formData.plot_dimension}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Covered Area
                  </label>
                  <input
                    type="text"
                    name="covered_area"
                    value={formData.covered_area}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Floors
                  </label>
                  <input
                    type="number"
                    name="floors"
                    value={formData.floors}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bedrooms
                  </label>
                  <input
                    type="number"
                    name="bed_rooms"
                    value={formData.bed_rooms}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bathrooms
                  </label>
                  <input
                    type="number"
                    name="bath_rooms"
                    value={formData.bath_rooms}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Associate to category (English)
                  </label>
                  <select
                    name="associated_house"
                    value={formData.associated_house}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                    required
                  >
                    <option value="">Select category</option>
                    {ASSOCIATED_HOUSE_OPTIONS.map((o) => (
                      <option key={o.en} value={o.en}>
                        {o.en === "others" ? "Others" : o.en}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    زمرہ سے منسلک کریں
                  </label>
                  <select
                    name="associated_house_ur"
                    value={formData.associated_house_ur}
                    onChange={handleChange}
                    dir="rtl"
                    className="w-full border border-gray-300 rounded-md p-2"
                  >
                    <option value="">منتخب کریں...</option>
                    {ASSOCIATED_HOUSE_OPTIONS.map((o) => (
                      <option key={o.ur} value={o.ur}>
                        {o.ur}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Standard Plan Bands (English)
                  </label>
                  <select
                    name="standard_plan_bands"
                    value={formData.standard_plan_bands}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                  >
                    <option value="">Select Standard Plan Band</option>
                    {STANDARD_PLAN_BAND_OPTIONS.map((o) => (
                      <option key={o.en} value={o.en}>
                        {o.en}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    معیاری پلان بینڈز
                  </label>
                  <select
                    name="standard_plan_band_ur"
                    value={formData.standard_plan_band_ur}
                    onChange={handleChange}
                    dir="rtl"
                    className="w-full border border-gray-300 rounded-md p-2"
                  >
                    <option value="">منتخب کریں...</option>
                    {STANDARD_PLAN_BAND_OPTIONS.map((o) => (
                      <option key={o.ur} value={o.ur}>
                        {o.ur}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Custom Url <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="customUrl"
                    value={formData.customUrl}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-md p-2"
                    required
                  />
                </div>
              </div>

              {/* Custom Pricing Plan - after Custom URL */}
              <div className="mb-4">
                <div
                  className="flex items-center bg-gray-50 p-3 rounded-md border border-gray-200 hover:bg-gray-100 cursor-pointer w-fit"
                  onClick={handleCustomPricingPlanChange}
                >
                  <input
                    type="checkbox"
                    id="custom_pricing_plan"
                    name="custom_pricing_plan"
                    checked={formData.custom_pricing_plan}
                    onChange={handleCustomPricingPlanChange}
                    className="mr-2 h-4 w-4 text-blue-500 focus:ring-blue-400"
                  />
                  <label
                    htmlFor="custom_pricing_plan"
                    className="cursor-pointer select-none"
                    onClick={(e) => e.preventDefault()}
                  >
                    Custom Pricing Plan
                  </label>
                </div>
              </div>

              {formData.custom_pricing_plan && (
                <div className="mb-4 p-4 border border-gray-200 rounded-md bg-gray-50">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">
                    Select Buy Plans
                  </h3>
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {buyPlansList.length === 0 ? (
                      <p className="text-sm text-gray-500">
                        Loading buy plans...
                      </p>
                    ) : (
                      buyPlansList.map((plan) => {
                        const planId = Number(plan.id);
                        const isChecked = (
                          formData.selected_buy_plan_ids || []
                        ).some((id) => Number(id) === planId);
                        return (
                          <div
                            key={plan.id}
                            className="flex items-center p-2 rounded border border-gray-200 bg-white hover:bg-gray-50 cursor-pointer"
                            onClick={() => handleBuyPlanCheckboxChange(planId)}
                          >
                            <input
                              type="checkbox"
                              id={`buy-plan-${plan.id}`}
                              checked={isChecked}
                              onChange={() =>
                                handleBuyPlanCheckboxChange(planId)
                              }
                              className="mr-3 h-4 w-4 text-blue-500 focus:ring-blue-400"
                            />
                            <label
                              htmlFor={`buy-plan-${plan.id}`}
                              className="cursor-pointer select-none text-sm flex-1"
                              onClick={(e) => e.preventDefault()}
                            >
                              {plan.name || `Plan #${plan.id}`}
                            </label>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <div className="border rounded-md mb-16">
                  <ReactQuill
                    theme="snow"
                    value={formData.description}
                    onChange={handleQuillChange}
                    className="h-40"
                  />
                </div>
                <label className="block text-sm font-medium text-indigo-700 text-right mb-1">
                  تفصیل
                </label>
                <div className="quill-rtl border border-indigo-200 rounded-md mb-16">
                  <style>{`.quill-rtl .ql-editor { direction: rtl; text-align: right; }`}</style>
                  <ReactQuill
                    theme="snow"
                    value={formData.description_ur}
                    onChange={handleQuillChangeUr}
                    className="h-40"
                  />
                </div>
              </div>

              {/* Feature Checkboxes */}
              <div className="mb-4 space-y-3">
                {/* English Row */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Features</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div
                      className="flex items-center bg-gray-50 p-3 rounded-md border border-gray-200 hover:bg-gray-100 cursor-pointer"
                      onClick={() => handleFeatureChange("servant_quarters")}
                    >
                      <input
                        type="checkbox"
                        id="servant_quarters_en"
                        name="servant_quarters"
                        checked={formData.servant_quarters}
                        onChange={() => handleFeatureChange("servant_quarters")}
                        className="mr-2 h-4 w-4 text-blue-500 focus:ring-blue-400"
                      />
                      <label
                        htmlFor="servant_quarters_en"
                        className="cursor-pointer select-none"
                        onClick={(e) => e.preventDefault()}
                      >
                        Servant Quarters
                      </label>
                    </div>
                    <div
                      className="flex items-center bg-gray-50 p-3 rounded-md border border-gray-200 hover:bg-gray-100 cursor-pointer"
                      onClick={() => handleFeatureChange("terrace")}
                    >
                      <input
                        type="checkbox"
                        id="terrace_en"
                        name="terrace"
                        checked={formData.terrace}
                        onChange={() => handleFeatureChange("terrace")}
                        className="mr-2 h-4 w-4 text-blue-500 focus:ring-blue-400"
                      />
                      <label
                        htmlFor="terrace_en"
                        className="cursor-pointer select-none"
                        onClick={(e) => e.preventDefault()}
                      >
                        Terrace
                      </label>
                    </div>
                    <div
                      className="flex items-center bg-gray-50 p-3 rounded-md border border-gray-200 hover:bg-gray-100 cursor-pointer"
                      onClick={() => handleFeatureChange("car_parking")}
                    >
                      <input
                        type="checkbox"
                        id="car_parking_en"
                        name="car_parking"
                        checked={formData.car_parking}
                        onChange={() => handleFeatureChange("car_parking")}
                        className="mr-2 h-4 w-4 text-blue-500 focus:ring-blue-400"
                      />
                      <label
                        htmlFor="car_parking_en"
                        className="cursor-pointer select-none"
                        onClick={(e) => e.preventDefault()}
                      >
                        Car Parking
                      </label>
                    </div>
                  </div>
                </div>

                {/* Urdu Row */}
                <div dir="rtl">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">خصوصیات</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div
                      className="flex items-center bg-indigo-50 p-3 rounded-md border border-indigo-200 hover:bg-indigo-100 cursor-pointer"
                      onClick={() => handleFeatureChange("servant_quarters")}
                    >
                      <input
                        type="checkbox"
                        id="servant_quarters_ur"
                        name="servant_quarters"
                        checked={formData.servant_quarters}
                        onChange={() => handleFeatureChange("servant_quarters")}
                        className="ml-2 h-4 w-4 text-indigo-500 focus:ring-indigo-400"
                      />
                      <label
                        htmlFor="servant_quarters_ur"
                        className="cursor-pointer select-none"
                        onClick={(e) => e.preventDefault()}
                      >
                        ملازم کا کمرہ
                      </label>
                    </div>
                    <div
                      className="flex items-center bg-indigo-50 p-3 rounded-md border border-indigo-200 hover:bg-indigo-100 cursor-pointer"
                      onClick={() => handleFeatureChange("terrace")}
                    >
                      <input
                        type="checkbox"
                        id="terrace_ur"
                        name="terrace"
                        checked={formData.terrace}
                        onChange={() => handleFeatureChange("terrace")}
                        className="ml-2 h-4 w-4 text-indigo-500 focus:ring-indigo-400"
                      />
                      <label
                        htmlFor="terrace_ur"
                        className="cursor-pointer select-none"
                        onClick={(e) => e.preventDefault()}
                      >
                        چھت
                      </label>
                    </div>
                    <div
                      className="flex items-center bg-indigo-50 p-3 rounded-md border border-indigo-200 hover:bg-indigo-100 cursor-pointer"
                      onClick={() => handleFeatureChange("car_parking")}
                    >
                      <input
                        type="checkbox"
                        id="car_parking_ur"
                        name="car_parking"
                        checked={formData.car_parking}
                        onChange={() => handleFeatureChange("car_parking")}
                        className="ml-2 h-4 w-4 text-indigo-500 focus:ring-indigo-400"
                      />
                      <label
                        htmlFor="car_parking_ur"
                        className="cursor-pointer select-none"
                        onClick={(e) => e.preventDefault()}
                      >
                        کار پارکنگ
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Upload Images <span className="text-lg text-red-500">*</span>
                </label>
                <div className="border border-dashed border-gray-300 p-4 rounded-md relative group">
                  <input
                    type="file"
                    multiple
                    onChange={handleImageChange}
                    className="hidden"
                    id="images"
                    accept="image/*"
                  />
                  <label
                    htmlFor="images"
                    className="flex flex-col items-center justify-center cursor-pointer"
                  >
                    <Upload size={24} className="text-gray-500 mb-2" />
                    <span className="text-sm text-gray-500">
                      Click to upload multiple images
                    </span>
                  </label>
                  {/* Tooltip */}
                  <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-10">
                    <div className="bg-gray-800 text-white text-xs rounded-md py-2 px-3 whitespace-nowrap shadow-lg">
                      Only the image with 1600*900 resolution can be added
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                    </div>
                  </div>
                </div>
              </div>

              {isEditing && existingImages.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Existing Images ({existingImages.length})
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {existingImages.map((image, index) => (
                      <div key={image.id || index} className="relative group">
                        <div
                          className={`relative h-24 border rounded-md flex items-center justify-center bg-gray-100 overflow-hidden ${
                            image.is_primary ? "ring-2 ring-blue-500" : ""
                          }`}
                        >
                          <img
                            src={image.image_url || image.url || image}
                            alt={`Existing ${index + 1}`}
                            className="h-full w-full object-cover"
                          />
                          {image.is_primary && (
                            <div className="absolute bottom-1 left-1/2 -translate-x-1/2 bg-blue-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap">
                              PRIMARY
                            </div>
                          )}
                        </div>

                        <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            type="button"
                            onClick={() => handleSetExistingPrimary(image.id)}
                            className={`p-1 rounded-full shadow-md ${
                              image.is_primary
                                ? "bg-blue-500 text-white"
                                : "bg-white text-gray-600 hover:bg-blue-100"
                            }`}
                            title={
                              image.is_primary
                                ? "Primary Image"
                                : "Set as Primary"
                            }
                          >
                            <Star
                              size={12}
                              fill={image.is_primary ? "currentColor" : "none"}
                            />
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setExistingImages((prev) =>
                                prev.filter((_, i) => i !== index),
                              )
                            }
                            className="bg-red-500 text-white rounded-full p-1"
                            title="Remove Image"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>

                        {!image.is_primary && (
                          <div className="mt-1">
                            <label className="block text-[10px] text-gray-600 mb-0.5">
                              Position
                            </label>
                            <input
                              type="text"
                              inputMode="numeric"
                              pattern="[1-9][0-9]*"
                              value={image.position ?? ""}
                              onChange={(e) =>
                                onExistingImagePositionChange(
                                  index,
                                  e.target.value,
                                )
                              }
                              className="w-full px-1 py-0.5 text-xs border border-gray-300 rounded"
                              placeholder="1"
                            />
                          </div>
                        )}
                        {image.is_primary && (
                          <p className="mt-1 text-[10px] text-blue-600 text-center font-medium">
                            Primary (not indexed)
                          </p>
                        )}
                        <ImageSeoFields
                          stacked
                          imageAlt={image.image_alt || ""}
                          imageTitle={image.image_title || ""}
                          onAltChange={(value) =>
                            onExistingImageSeoChange(index, "image_alt", value)
                          }
                          onTitleChange={(value) =>
                            onExistingImageSeoChange(index, "image_title", value)
                          }
                          className="mt-2"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Display selected images */}
              {formData.images.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Selected Images ({formData.images.length})
                  </h4>
                  <div className="grid grid-cols-4 gap-2">
                    {formData.images.map((file, index) => (
                      <div key={index} className="relative">
                        <div
                          className={`relative h-24 border rounded-md flex items-center justify-center bg-gray-100 overflow-hidden ${
                            index === formData.primaryImageIndex
                              ? "ring-2 ring-blue-500"
                              : ""
                          }`}
                        >
                          {formData.imagePreviews &&
                          formData.imagePreviews[index] ? (
                            <img
                              src={formData.imagePreviews[index]}
                              alt={file.name || `Image ${index + 1}`}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <p className="text-xs text-gray-500 p-2 truncate">
                              {file.name || "Image " + (index + 1)}
                            </p>
                          )}

                          {/* Primary Image Star */}
                          {index === formData.primaryImageIndex && (
                            <div className="absolute top-1 left-1 bg-blue-500 text-white rounded-full p-1 shadow-md">
                              <Star size={12} fill="white" />
                            </div>
                          )}

                          {/* Set Primary Button */}
                          {index !== formData.primaryImageIndex && (
                            <button
                              type="button"
                              onClick={() => setPrimaryImage(index)}
                              className="absolute bottom-1 left-1 bg-white text-gray-600 hover:bg-blue-100 rounded-full p-1 shadow-md transition-colors"
                              title="Set as Primary Image"
                            >
                              <Star size={12} />
                            </button>
                          )}
                        </div>

                        {/* Remove Button */}
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600 transition-colors z-10"
                        >
                          <Trash2 size={12} />
                        </button>

                        {index !== formData.primaryImageIndex ? (
                          <div className="mt-1">
                            <label className="block text-[10px] text-gray-600 mb-0.5">
                              Position
                            </label>
                            <input
                              type="text"
                              inputMode="numeric"
                              pattern="[1-9][0-9]*"
                              value={formData.imagePositions?.[index] ?? ""}
                              onChange={(e) =>
                                onImagePositionChange(index, e.target.value)
                              }
                              className="w-full px-1 py-0.5 text-xs border border-gray-300 rounded"
                              placeholder="1"
                            />
                          </div>
                        ) : (
                          <p className="mt-1 text-[10px] text-blue-600 text-center font-medium">
                            Primary (not indexed)
                          </p>
                        )}
                        <ImageSeoFields
                          stacked
                          imageAlt={formData.imageAlts?.[index] || ""}
                          imageTitle={formData.imageTitles?.[index] || ""}
                          onAltChange={(value) =>
                            onNewImageSeoChange(index, "image_alt", value)
                          }
                          onTitleChange={(value) =>
                            onNewImageSeoChange(index, "image_title", value)
                          }
                          className="mt-2"
                        />
                      </div>
                    ))}
                  </div>

                  {/* Primary Image Info */}
                  {formData.images.length > 1 && (
                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
                      <p className="text-xs text-blue-700">
                        <Star size={12} className="inline mr-1" />
                        Primary image selected:{" "}
                        {formData.images[formData.primaryImageIndex]?.name ||
                          `Image ${formData.primaryImageIndex + 1}`}
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setIsEditing(false);
                    setEditId(null);
                    setExistingImages([]);
                    resetForm();
                  }}
                  className="btn btn-reset-filter justify-center"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`btn justify-center ${
                    isEditing ? "btn-edit text-white" : "btn-primary"
                  } ${isSubmitting ? "opacity-75 cursor-not-allowed" : ""}`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Processing...
                    </>
                  ) : isEditing ? (
                    "Update"
                  ) : (
                    "Save"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isSimilarModalOpen && (
        <SimilarItemsModal
          isOpen={isSimilarModalOpen}
          onClose={closeSimilarModal}
          sourceId={selectedDesignForSimilar}
          sourceType="house_design"
        />
      )}

      {/* Add Popular Design Modal */}
      {showPopularDesignModal && (
        <AddPopularDesign
          isOpen={showPopularDesignModal}
          onClose={() => setShowPopularDesignModal(false)}
        />
      )}

      {isPositionModalOpen && selectedDesignForPosition && (
        <RecordPosition
          isOpen={isPositionModalOpen}
          onClose={closePositionModal}
          currentPosition={selectedDesignForPosition.currentPosition}
          recordId={selectedDesignForPosition.recordId}
          onUpdatePosition={handleUpdatePosition}
          moduleName="House Design"
        />
      )}
    </div>
  );
};

export default HouseDesign;
